import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { FaSearch, FaTrash, FaUsers, FaUserTie } from 'react-icons/fa';
import { upsertDraft, deleteDraft, makeDraftId } from '../../utils/eventDrafts';
import Spinner from '../common/Spinner';

const CreateEvent = ({ onSuccess, draftToEdit, onDraftsChanged }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        synopsis: '',
        date: '',
        time: '',
        venue: '',
        poster: null
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [students, setStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchingStudents, setFetchingStudents] = useState(false);
    const [showSectionAdd, setShowSectionAdd] = useState(false);
    
    const [sectionFilters, setSectionFilters] = useState({
        year: '',
        program: '',
        branch: '',
        section: ''
    });
    
    const [programOptions, setProgramOptions] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const [yearOptions] = useState(['1', '2', '3', '4', '5']);
    const [sectionOptions] = useState(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']);

    // Coordinator States
    const [selectedCoordinators, setSelectedCoordinators] = useState([]);
    const [coordinatorSearch, setCoordinatorSearch] = useState('');
    const [showCoordinatorDropdown, setShowCoordinatorDropdown] = useState(false);
    const [allStaff, setAllStaff] = useState([]);
    const [fetchingStaff, setFetchingStaff] = useState(false);
    
    const coordinatorRef = useRef(null);
    const studentRef = useRef(null);
    const [editingDraftId, setEditingDraftId] = useState(draftToEdit?.id || null);

    useEffect(() => {
        fetchStudents();
        fetchStaff();
        fetchProgramsAndBranches();
    }, []);

    useEffect(() => {
        if (draftToEdit) {
            setFormData(prev => ({ ...prev, ...draftToEdit.formData, poster: null }));
            setSelectedStudents(draftToEdit.selectedStudents || []);
            setSelectedCoordinators(draftToEdit.selectedCoordinators || []);
            setEditingDraftId(draftToEdit.id);
            toast.success('📝 Draft loaded for editing');
        }
    }, [draftToEdit]);

    const resetForm = () => {
        setFormData({ name: '', synopsis: '', date: '', time: '', venue: '', poster: null });
        setSelectedStudents([]);
        setSelectedCoordinators([]);
        setEditingDraftId(null);
    };

    const saveDraft = () => {
        if (!formData.name) {
            toast.error('Please at least enter an event name before saving as a draft');
            return;
        }
        const draft = {
            id: editingDraftId || makeDraftId(),
            formData: {
                name: formData.name,
                synopsis: formData.synopsis,
                date: formData.date,
                time: formData.time,
                venue: formData.venue
            },
            selectedStudents,
            selectedCoordinators,
            savedAt: new Date().toISOString()
        };
        upsertDraft(user?._id, draft);
        if (onDraftsChanged) onDraftsChanged();
        resetForm();
        toast.success(formData.poster
            ? '💾 Saved as draft! (Note: the poster image is not saved with drafts — you\'ll need to re-attach it later). Start your next event whenever you\'re ready.'
            : '💾 Saved as draft! Start your next event whenever you\'re ready.');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (coordinatorRef.current && !coordinatorRef.current.contains(event.target)) {
                setShowCoordinatorDropdown(false);
            }
            if (studentRef.current && !studentRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchStudents = async () => {
        setFetchingStudents(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('Please login again');
                window.location.href = '/login';
                return;
            }
            const response = await api.get('/staff/students');
            if (response.data.success && response.data.users) {
                setStudents(response.data.users);
                const programs = [...new Set(response.data.users.map(s => s.program).filter(Boolean))];
                const branches = [...new Set(response.data.users.map(s => s.branch).filter(Boolean))];
                setProgramOptions(programs);
                setBranchOptions(branches);
            }
        } catch (error) {
            toast.error('Failed to fetch students');
        } finally {
            setFetchingStudents(false);
        }
    };

    const fetchStaff = async () => {
        setFetchingStaff(true);
        try {
            const response = await api.get('/admin/staff-list');
            setAllStaff(response.data.users || []);
        } catch (error) {
            console.error('Failed to fetch staff:', error);
        } finally {
            setFetchingStaff(false);
        }
    };

    const fetchProgramsAndBranches = async () => {
        try {
            const response = await api.get('/admin/users?role=student&status=active');
            const students = response.data.users || [];
            const programs = [...new Set(students.map(s => s.program).filter(Boolean))];
            const branches = [...new Set(students.map(s => s.branch).filter(Boolean))];
            setProgramOptions(programs);
            setBranchOptions(branches);
        } catch (error) {
            console.error('Failed to fetch programs/branches:', error);
        }
    };

    const filteredStudents = students.filter(s =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.regNo?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ✅ Filter staff for coordinators - exclude current user and limit to 4 (since creator is primary)
    const filteredCoordinators = coordinatorSearch 
        ? allStaff.filter(s =>
            s._id !== user?._id &&
            (s.name?.toLowerCase().includes(coordinatorSearch.toLowerCase()) ||
            s.email?.toLowerCase().includes(coordinatorSearch.toLowerCase()))
          )
        : allStaff.filter(s => s._id !== user?._id);

    const selectStudent = (student) => {
        if (!selectedStudents.find(s => s._id === student._id)) {
            setSelectedStudents([...selectedStudents, student]);
            toast.success(`Added ${student.name}`);
        }
        setSearchTerm('');
        setShowDropdown(false);
    };

    const removeStudent = (id) => {
        setSelectedStudents(selectedStudents.filter(s => s._id !== id));
    };

    const selectCoordinator = (staff) => {
        // ✅ Max 19 secondary coordinators (creator is primary = total 20)
        if (selectedCoordinators.length >= 19) {
            toast.error('Maximum 19 secondary coordinators allowed (plus you as primary = 20 total)');
            return;
        }
        if (!selectedCoordinators.find(c => c._id === staff._id)) {
            setSelectedCoordinators([...selectedCoordinators, staff]);
            toast.success(`Added ${staff.name} as secondary coordinator`);
        }
        setCoordinatorSearch('');
        setShowCoordinatorDropdown(false);
    };

    const removeCoordinator = (id) => {
        setSelectedCoordinators(selectedCoordinators.filter(c => c._id !== id));
    };

    const addSectionWise = () => {
        const { year, program, branch, section } = sectionFilters;
        if (!program || !branch || !section) {
            toast.error('Please select Program, Branch, and Section');
            return;
        }

        const filtered = students.filter(s => 
            (year ? s.year === year : true) &&
            s.program?.toLowerCase() === program.toLowerCase() &&
            s.branch?.toLowerCase() === branch.toLowerCase() &&
            s.section?.toLowerCase() === section.toLowerCase() &&
            s.status === 'active'
        );

        if (filtered.length === 0) {
            toast.error('No students found for the selected criteria');
            return;
        }

        const newStudents = filtered.filter(s => !selectedStudents.find(sel => sel._id === s._id));
        setSelectedStudents([...selectedStudents, ...newStudents]);
        toast.success(`Added ${newStudents.length} students from ${program} - ${branch} - Section ${section}`);
        setShowSectionAdd(false);
        setSectionFilters({ year: '', program: '', branch: '', section: '' });
    };

    const checkDuplicateEvent = async (name) => {
        try {
            const response = await api.get('/events');
            const existing = response.data.events.find(e => e.name.toLowerCase() === name.toLowerCase());
            if (existing) {
                toast.error(`❌ Event "${name}" already exists! Please use a different name.`);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Duplicate check error:', error);
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.date || !formData.time) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (selectedStudents.length === 0) {
            toast.error('Please select at least one student');
            return;
        }

        const isDuplicate = await checkDuplicateEvent(formData.name);
        if (isDuplicate) return;

        setLoading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('synopsis', formData.synopsis || 'No synopsis provided');
            formDataToSend.append('date', formData.date);
            formDataToSend.append('time', formData.time);
            formDataToSend.append('venue', formData.venue || '');
            formDataToSend.append('students', JSON.stringify(selectedStudents.map(s => s._id)));
            formDataToSend.append('coordinators', JSON.stringify(selectedCoordinators.map(c => c._id)));
            if (formData.poster) {
                formDataToSend.append('poster', formData.poster);
            }

            await api.post('/events', formDataToSend, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            toast.success(`✅ Event "${formData.name}" created with ${selectedStudents.length} students and ${selectedCoordinators.length + 1} coordinators (including you)!`);
            if (editingDraftId) {
                deleteDraft(user?._id, editingDraftId);
                if (onDraftsChanged) onDraftsChanged();
            }
            resetForm();
            if (onSuccess) onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create event');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card">
            <h3 className="card-title">Create New Event</h3>
            <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div className="form-group">
                        <label>Event Name *</label>
                        <input className="form-control" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Enter event name" required />
                    </div>
                    <div className="form-group">
                        <label>Venue</label>
                        <input className="form-control" value={formData.venue} onChange={e => setFormData({...formData, venue: e.target.value})} placeholder="Enter venue" />
                    </div>
                    <div className="form-group">
                        <label>Date *</label>
                        <input type="date" className="form-control" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                    </div>
                    <div className="form-group">
                        <label>Time *</label>
                        <input type="time" className="form-control" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} required />
                    </div>
                </div>

                <div className="form-group">
                    <label>Event Synopsis</label>
                    <textarea className="form-control" rows="3" value={formData.synopsis} onChange={e => setFormData({...formData, synopsis: e.target.value})} placeholder="Brief description of the event" />
                </div>

                <div className="form-group">
                    <label>Event Poster</label>
                    <input type="file" className="form-control" accept="image/*" onChange={e => setFormData({...formData, poster: e.target.files[0]})} />
                    {formData.poster && <p style={{ fontSize: '12px', color: 'green', marginTop: '5px' }}>✅ Selected: {formData.poster.name}</p>}
                </div>

                {/* Search Students */}
                <div className="form-group" ref={studentRef}>
                    <label>Search Students</label>
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '2px solid var(--srm-border)', borderRadius: '8px', background: 'white' }}>
                            <FaSearch style={{ marginLeft: '10px', color: '#999' }} />
                            <input type="text" className="form-control" style={{ border: 'none', background: 'transparent' }}
                                value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
                                onFocus={() => { setShowDropdown(true); if (students.length === 0) fetchStudents(); }}
                                placeholder="Type name or registration number..." />
                            <span style={{ marginRight: '10px', fontSize: '12px', color: '#999' }}>({students.length} students)</span>
                        </div>
                        {showDropdown && searchTerm && filteredStudents.length > 0 && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--srm-border)', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, marginTop: '5px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                {filteredStudents.map(s => (
                                    <div key={s._id} onClick={() => selectStudent(s)} style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                                        <strong>{s.name}</strong> <span style={{ color: '#666', marginLeft: '10px' }}>{s.regNo || 'N/A'}</span>
                                        <span style={{ color: '#999', marginLeft: '10px', fontSize: '12px' }}>{s.branch || 'N/A'}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Section Wise Add */}
                <div className="form-group">
                    <button type="button" className="btn btn-info" onClick={() => setShowSectionAdd(!showSectionAdd)} style={{ marginBottom: '10px' }}>
                        <FaUsers /> {showSectionAdd ? 'Cancel' : 'Add Students by Section'}
                    </button>
                    {showSectionAdd && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 600 }}>Year</label>
                                <select className="form-control" value={sectionFilters.year} onChange={e => setSectionFilters({...sectionFilters, year: e.target.value})} style={{ padding: '6px 10px' }}>
                                    <option value="">All Years</option>
                                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 600 }}>Program</label>
                                <select className="form-control" value={sectionFilters.program} onChange={e => setSectionFilters({...sectionFilters, program: e.target.value})} style={{ padding: '6px 10px' }}>
                                    <option value="">Select Program</option>
                                    {programOptions.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 600 }}>Branch</label>
                                <select className="form-control" value={sectionFilters.branch} onChange={e => setSectionFilters({...sectionFilters, branch: e.target.value})} style={{ padding: '6px 10px' }}>
                                    <option value="">Select Branch</option>
                                    {branchOptions.map(b => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 600 }}>Section</label>
                                <select className="form-control" value={sectionFilters.section} onChange={e => setSectionFilters({...sectionFilters, section: e.target.value})} style={{ padding: '6px 10px' }}>
                                    <option value="">Select Section</option>
                                    {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px' }}>
                                <button type="button" className="btn btn-success" onClick={addSectionWise}>Add Section Students</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Event Coordinators - Creator is automatically Primary */}
                <div className="form-group" ref={coordinatorRef}>
                    <label>
                        <FaUserTie /> Secondary Coordinators (Optional - Max 19)
                    </label>
                    <div style={{ 
                        fontSize: '12px', 
                        color: '#17a2b8', 
                        marginBottom: '8px',
                        padding: '8px 12px',
                        background: '#e8f4fd',
                        borderRadius: '6px',
                        borderLeft: '3px solid #17a2b8'
                    }}>
                        👤 You are the <strong>Primary Coordinator</strong> (automatically added)
                        <br />
                        <span style={{ fontSize: '11px', color: '#666' }}>
                            Add up to 19 secondary coordinators (total 20 including you)
                        </span>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: '2px solid var(--srm-border)', borderRadius: '8px', background: 'white' }}>
                            <FaSearch style={{ marginLeft: '10px', color: '#999' }} />
                            <input
                                type="text"
                                className="form-control"
                                style={{ border: 'none', background: 'transparent' }}
                                value={coordinatorSearch}
                                onChange={(e) => {
                                    setCoordinatorSearch(e.target.value);
                                    setShowCoordinatorDropdown(true);
                                }}
                                onFocus={() => {
                                    setShowCoordinatorDropdown(true);
                                    if (allStaff.length === 0) fetchStaff();
                                }}
                                placeholder="Search staff to add as secondary coordinator..."
                                disabled={selectedCoordinators.length >= 19}
                            />
                            <span style={{ marginRight: '10px', fontSize: '12px', color: '#999' }}>
                                ({selectedCoordinators.length}/19)
                            </span>
                        </div>
                        {showCoordinatorDropdown && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                background: 'white',
                                border: '1px solid var(--srm-border)',
                                borderRadius: '8px',
                                maxHeight: '150px',
                                overflowY: 'auto',
                                zIndex: 999,
                                marginTop: '5px',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                            }}>
                                {filteredCoordinators.length === 0 ? (
                                    <div style={{ padding: '15px', textAlign: 'center', color: '#666' }}>
                                        {coordinatorSearch ? 'No staff found matching your search' : 'No other staff available'}
                                    </div>
                                ) : (
                                    filteredCoordinators.map(s => (
                                        <div
                                            key={s._id}
                                            onClick={() => selectCoordinator(s)}
                                            style={{ 
                                                padding: '10px 15px', 
                                                cursor: 'pointer', 
                                                borderBottom: '1px solid #eee',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <div>
                                                <strong>{s.name}</strong>
                                                <span style={{ color: '#666', fontSize: '12px', marginLeft: '10px' }}>{s.email}</span>
                                            </div>
                                            <span style={{ color: '#999', fontSize: '11px' }}>{s.department || 'N/A'}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                    {selectedCoordinators.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                            <span style={{
                                background: '#0a1e3c',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '13px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                ⭐ {user?.name} (Primary Coordinator)
                            </span>
                            {selectedCoordinators.map(c => (
                                <span key={c._id} style={{
                                    background: '#17a2b8',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: '20px',
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    👤 {c.name}
                                    <span onClick={() => removeCoordinator(c._id)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
                                </span>
                            ))}
                        </div>
                    )}
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                        💡 Secondary coordinators will also receive attendance reports
                    </div>
                </div>

                {/* Selected Students */}
                {selectedStudents.length > 0 && (
                    <div className="form-group">
                        <label>Selected Students ({selectedStudents.length})</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px', background: 'var(--srm-gray)', borderRadius: '8px' }}>
                            {selectedStudents.map(s => (
                                <span key={s._id} style={{ background: 'var(--srm-blue)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {s.name} ({s.regNo})
                                    <span onClick={() => removeStudent(s._id)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {editingDraftId && (
                    <p style={{ fontSize: '12px', color: '#f57c00', marginTop: '10px' }}>
                        📝 Editing a saved draft — saving again updates it, or submit to publish it as a real event.
                    </p>
                )}
                <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                    <button type="button" className="btn btn-warning" onClick={saveDraft}>💾 Save Draft</button>
                    <button type="submit" className="btn btn-success" disabled={loading}>{loading ? <><Spinner /> Creating...</> : '✅ Create Event'}</button>
                </div>
            </form>
        </div>
    );
};

export default CreateEvent;